import React, { useState, useEffect } from "react";
import {
  TextField,
  Autocomplete,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useTranslate } from "@refinedev/core";
import { useDebounce } from "../../hooks/useDebounce";

interface AddressAutocompleteProps {
  onAddressSelect: (result: GeoapifyResult) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

export interface GeoapifyResult {
  name?: string;
  country?: string;
  country_code?: string;
  state?: string;
  county?: string;
  city?: string;
  postcode?: string;
  street?: string;
  housenumber?: string;
  formatted?: string;
  lat: number;
  lon: number;
  result_type?: string;
  place_id?: string;
  address_line1?: string;
  address_line2?: string;
  [key: string]: any; // Allow for additional properties returned by the API
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  onAddressSelect,
  label,
  placeholder,
  required = false,
  error = false,
  helperText = "",
}) => {
  const translate = useTranslate();
  const resolvedLabel = label ?? translate('address.label');
  const resolvedPlaceholder = placeholder ?? translate('address.placeholder');
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<GeoapifyResult[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedValue = useDebounce(inputValue, 500);

  useEffect(() => {
    let active = true;

    if (!debouncedValue || debouncedValue.length < 3) {
      setOptions([]);
      return undefined;
    }

    const fetchAddressSuggestions = async () => {
      setLoading(true);
            try {
        const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;

        if (!apiKey) {
          console.error("REACT_APP_GEOAPIFY_API_KEY is not set in environment variables");
          setOptions([]);
          return;
        }

        const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
          debouncedValue
        )}&format=json&apiKey=${apiKey}`;

        const response = await fetch(url);
        const result = await response.json();

        if (active && result.results) {
          // Make sure each result has the required properties
          const validResults = result.results.map((res: any) => ({
            ...res,
            lat: res.lat || 0,
            lon: res.lon || 0,
            formatted: res.formatted || `${res.lat}, ${res.lon}`
          }));
          setOptions(validResults);
        }
      } catch (error: any) {
        console.error("Error fetching address suggestions:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAddressSuggestions();

    return () => {
      active = false;
    };
  }, [debouncedValue]);

  // Create a more robust isOptionEqualToValue function
  const isEqualOption = (option: GeoapifyResult, value: GeoapifyResult): boolean => {
    // If the objects are strictly equal, return true
    if (option === value) return true;

    // If either one is null or undefined but not both, return false
    if (!option || !value) return false;

    // Compare by place_id if available
    if (option.place_id && value.place_id) {
      return option.place_id === value.place_id;
    }

    // If both have lat/lon coordinates, compare them
    if (typeof option.lat === 'number' && typeof option.lon === 'number' &&
        typeof value.lat === 'number' && typeof value.lon === 'number') {
      // Allow small difference for floating point precision
      const latDiff = Math.abs(option.lat - value.lat);
      const lonDiff = Math.abs(option.lon - value.lon);
      return latDiff < 0.0000001 && lonDiff < 0.0000001;
    }

    // If formatted addresses are available, compare them
    if (option.formatted && value.formatted) {
      return option.formatted === value.formatted;
    }

    // If all else fails, consider them different
    return false;
  };

  return (
    <Autocomplete
      id="address-autocomplete"
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      loading={loading}
      value={null}
      noOptionsText={
        !inputValue
          ? "Start typing to search"
          : inputValue.length < 3
          ? "Type at least 3 characters"
          : "No results found"
      }
      getOptionLabel={(option) => option.formatted || ""}
      isOptionEqualToValue={isEqualOption}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.place_id || `${option.lat}-${option.lon}`}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body1">{option.formatted}</Typography>
            {option.result_type && (
              <Typography variant="caption" color="text.secondary">
                {option.result_type}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      onChange={(_, newValue) => {
        if (newValue) {
          onAddressSelect(newValue);
          setTimeout(() => {
            setInputValue("");
          }, 100);
        }
      }}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={resolvedLabel}
          placeholder={resolvedPlaceholder}
          required={required}
          error={error}
          helperText={helperText}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

export default AddressAutocomplete;
